import { useEffect, useRef } from "react";
import type { OperatorTabletWorkspace } from "./useOperatorTabletWorkspace";
import { dateTime, durationBetween } from "../production-time";
import { operatorEventDuration } from "../operator-status-view";
import { ClipboardList, MessageSquareWarning, PauseCircle, Plus, X } from "lucide-react";
import { hasVisibleWorkOrder } from "../work-order-display";
import { OperatorHistoryControls } from "../OperatorHistoryControls";
import { amount } from "./quantity-format";
import { entryLabel } from "../production-entry-label";

export function OperatorHistoryPanel({ selected, openDowntime, visibleDowntimeReason, recordsOpen, setRecordsOpen, historyCounts, latestVisibleItem, setScrapSheet, shortStopMilliseconds, history, recordTimeline, latestEntry, setCorrectionEntry }: OperatorTabletWorkspace["historyPanel"]) {
  const closeButton = useRef<HTMLButtonElement>(null);
  const triggerButton = useRef<HTMLButtonElement>(null);
  const openingControl = useRef<HTMLElement | null>(null);
  const movingToMaterials = useRef(false);
  const panelVisible = Boolean(selected && !openDowntime && !visibleDowntimeReason && recordsOpen);
  useEffect(() => {
    if (!panelVisible) return;
    openingControl.current = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    movingToMaterials.current = false;
    closeButton.current?.focus();
    return () => {
      if (movingToMaterials.current) return;
      const target = openingControl.current;
      if (target?.isConnected && target !== document.body) target.focus();
      else triggerButton.current?.focus();
    };
  }, [panelVisible]);
  return (
    <>
      {selected && !openDowntime && !visibleDowntimeReason && (selected.status !== "COMPLETED" || recordsOpen) ? (
        <div
          className={`operator-records-layer${recordsOpen ? " is-open" : ""}`}
        >
          <div
            className="operator-records-backdrop"
            onMouseDown={() => setRecordsOpen(false)}
          />
          <div className="operator-records-dock">
            {selected.status === "ACTIVE" || recordsOpen ? <button
              ref={triggerButton}
              type="button"
              className="operator-records-button"
              aria-controls="operator-records-panel"
              aria-expanded={recordsOpen}
              aria-label={
                recordsOpen ? "Kayıt panelini kapat" : "Kayıt panelini aç"
              }
              onClick={() => setRecordsOpen((open) => !open)}
            >
              <ClipboardList size={19} />
              <span>
                <strong>
                  {recordsOpen
                    ? "Kayıtları kapat"
                    : `Kayıtlar (${historyCounts?.allVisibleCount ?? 0})`}
                </strong>
                {!recordsOpen && latestVisibleItem ? (
                  <small>
                    Son:{" "}
                    {latestVisibleItem.kind === "entry"
                      ? `${entryLabel(latestVisibleItem.entry.type)} · ${amount(latestVisibleItem.entry.quantity, latestVisibleItem.entry.unit)}`
                      : latestVisibleItem.kind === "downtime"
                        ? `Duruş · ${latestVisibleItem.downtime.reasonName ?? "Neden bekleniyor"}`
                        : `Şikayet · ${latestVisibleItem.complaint.reviewedAt ? "İncelendi" : "Amir bekleniyor"}`}
                  </small>
                ) : null}
              </span>
            </button> : null}
            <aside
              id="operator-records-panel"
              className="operator-records-drawer"
              aria-label="Hareket geçmişi"
              aria-hidden={!recordsOpen}
              inert={!recordsOpen}
              onKeyDown={(event) => {
                if (event.key === "Escape") {
                  event.stopPropagation();
                  setRecordsOpen(false);
                }
              }}
            >
              <header>
                <div>
                  <small>{hasVisibleWorkOrder(selected) ? `İŞ EMRİ ${selected.workOrderNo}` : "PLANLI ÜRETİM"}</small>
                  <h2>Hareket geçmişi</h2>
                  <button type="button" className="operator-history-secondary-action" onClick={() => {
                    movingToMaterials.current = true;
                    setRecordsOpen(false);
                    setScrapSheet("MATERIAL");
                  }}>Malzeme ıskarta kayıtları</button>
                  <span>{historyCounts?.allVisibleCount ?? 0} kronolojik hareket</span>
                </div>
                <button
                  type="button"
                  ref={closeButton}
                  className="operator-records-close"
                  aria-label="Kayıtları kapat"
                  onClick={() => setRecordsOpen(false)}
                >
                  <X size={22} aria-hidden="true" />
                </button>
              </header>
              <div className="operator-record-summary" aria-label="Kayıt özeti">
                <span><b>{historyCounts?.manualNonVoidedCount ?? 0}</b> miktar kaydı</span>
                <span><b>{historyCounts?.nonShortDowntimeCount ?? 0}</b> duruş</span>
                {historyCounts?.shortDowntimeCount ? <span><b>{historyCounts.shortDowntimeCount}</b> kısa duruş · {operatorEventDuration(shortStopMilliseconds)}</span> : null}
                <span><b>{historyCounts?.complaintCount ?? 0}</b> şikayet</span>
              </div>
              <OperatorHistoryControls loading={history.loading} loaded={history.loaded} error={history.error}
                count={history.items.length} hasMore={history.nextCursor !== null} onRefresh={() => void history.refresh()}
                onRetry={history.retry} onMore={history.loadMore} />
              <div className="operator-record-list" tabIndex={recordsOpen ? 0 : -1} role="region" aria-label="Kronolojik kayıtlar" aria-busy={history.loading}>
                {recordTimeline.length ? (
                  recordTimeline.map((item) =>
                    item.kind === "entry" ? (
                      <article
                        key={`entry-${item.entry.id}`}
                        className={`is-${item.entry.type.toLowerCase()}${item.entry.voidedAt ? " is-voided" : ""}`}
                      >
                        <i className="operator-timeline-marker">
                          {item.entry.type === "PRODUCTION" ? <Plus size={16} /> : <X size={15} />}
                        </i>
                        <div className="operator-timeline-content">
                          <header>
                            <span>
                              <small>{entryLabel(item.entry.type)}</small>
                              <strong>{amount(item.entry.quantity, item.entry.unit)}</strong>
                            </span>
                            <time>{dateTime(item.entry.occurredAt)}</time>
                          </header>
                          <p>{item.entry.reasonName ?? item.entry.note ?? "Açıklama yok"}</p>
                          {item.entry.type === "SCRAP" && item.entry.scrapDisposition ? (
                            <em>{item.entry.scrapDisposition === "ANIMAL_FEED" ? "Hayvan yemine ayrıldı" : "Yeniden değerlendirilecek"}</em>
                          ) : null}
                          <footer><span>{item.entry.enteredByName}</span></footer>
                          {item.entry.voidedAt ? (
                            <small className="operator-record-void">İptal edildi · {item.entry.voidReason}</small>
                          ) : item.entry.enteredByName !== "PLC sayacı" && item.entry.id === latestEntry?.id && Date.now() - new Date(item.entry.occurredAt).getTime() <= 5 * 60_000 ? (
                            <button onClick={() => setCorrectionEntry(item.entry)}>Hatalı kaydı geri al</button>
                          ) : null}
                        </div>
                      </article>
                    ) : item.kind === "downtime" ? (
                      <article
                        key={`downtime-${item.downtime.id}`}
                        className={`is-downtime${item.downtime.endedAt ? "" : " is-active"}`}
                      >
                        <i className="operator-timeline-marker"><PauseCircle size={16} /></i>
                        <div className="operator-timeline-content">
                          <header>
                            <span>
                              <small>Duruş</small>
                              <strong>{item.downtime.reasonName ?? "Neden bekleniyor"}</strong>
                            </span>
                          </header>
                          <p>
                            <time dateTime={item.downtime.startedAt}>{dateTime(item.downtime.startedAt)}</time>
                            {item.downtime.endedAt
                              ? <> → <time dateTime={item.downtime.endedAt}>{dateTime(item.downtime.endedAt)}</time></>
                              : " · Duruş devam ediyor"}
                          </p>
                          <em>{item.downtime.endedAt ? durationBetween(item.downtime.startedAt, item.downtime.endedAt) : "Sayaç çalışıyor"}</em>
                          {item.downtime.note ? <p>{item.downtime.note}</p> : null}
                          <footer><span>{item.downtime.startedByName}</span></footer>
                        </div>
                      </article>
                    ) : (
                      <article key={`complaint-${item.complaint.id}`} className={`is-complaint${item.complaint.reviewedAt ? " is-reviewed" : " is-active"}`}>
                        <i className="operator-timeline-marker"><MessageSquareWarning size={16} /></i>
                        <div className="operator-timeline-content">
                          <header>
                            <span><small>Şikayet</small><strong>{item.complaint.reviewedAt ? "İncelendi" : "Amir incelemesi bekleniyor"}</strong></span>
                            <time>{dateTime(item.complaint.reportedAt)}</time>
                          </header>
                          <p>{item.complaint.description}</p>
                          <footer><span>{item.complaint.reportedByName}</span></footer>
                          {item.complaint.reviewedAt ? (
                            <div className="operator-complaint-review">
                              <small>{dateTime(item.complaint.reviewedAt)} · {item.complaint.reviewedByName}</small>
                              {item.complaint.reviewNote ? <p>{item.complaint.reviewNote}</p> : null}
                            </div>
                          ) : null}
                        </div>
                      </article>
                    ),
                  )
                ) : history.loaded && !history.loading && !history.error ? (
                  <p className="operator-records-empty">
                    Henüz üretim hareketi kaydedilmedi.
                  </p>
                ) : null}
              </div>
            </aside>
          </div>
        </div>
      ) : null}
    </>
  );
}
